import { useCallback, useEffect, useRef } from 'react';
import { offlineService, reportsService } from '../../../services/supabaseService';
import { REPORT_SYNC_DEBOUNCE_MS } from '../../../domain/sync/reportSyncLifecycle';
import {
  executeReportSync,
  executeLegacyReportSync
} from '../../../domain/sync/reportSyncOrchestrator';
import { createDebouncedScheduler } from '../../../domain/operations/expirySweepScheduler';

export function useReportSyncLifecycle({
  currentBusinessId,
  products,
  sales,
  purchases,
  orders,
  customers,
  suppliers,
  expiredProducts
}) {
  const reportSyncSchedulerRef = useRef(null);
  const reportSyncInFlightRef = useRef(false);

  if (!reportSyncSchedulerRef.current) {
    reportSyncSchedulerRef.current = createDebouncedScheduler(REPORT_SYNC_DEBOUNCE_MS);
  }

  const syncReportsWithData = useCallback(async (businessId, currentData) => {
    if (!businessId || reportSyncInFlightRef.current) {
      return;
    }

    reportSyncInFlightRef.current = true;
    try {
      await executeReportSync({
        businessId,
        currentData,
        reportsService,
        offlineService
      });
    } catch (error) {
      if (
        !error?.message?.includes('406') &&
        !error?.message?.includes('Not Acceptable')
      ) {
        throw error;
      }
    } finally {
      reportSyncInFlightRef.current = false;
    }
  }, []);

  const scheduleReportSyncAfterSave = useCallback(
    async (businessId) => {
      if (!businessId) return;

      const { localStorageManager } = await import('../../../services/localStorageManager');
      reportSyncSchedulerRef.current.schedule(() => {
        const currentData = localStorageManager.getBusinessData(businessId);
        if (currentData) {
          void syncReportsWithData(businessId, currentData);
        }
      });
    },
    [syncReportsWithData]
  );

  const syncReports = useCallback(async () => {
    await executeLegacyReportSync({
      businessId: currentBusinessId,
      products,
      sales,
      purchases,
      orders,
      customers,
      suppliers,
      expiredProducts,
      reportsService
    });
  }, [
    currentBusinessId,
    products,
    sales,
    purchases,
    orders,
    customers,
    suppliers,
    expiredProducts
  ]);

  useEffect(() => {
    return () => reportSyncSchedulerRef.current?.cancel();
  }, []);

  return {
    scheduleReportSyncAfterSave,
    syncReports
  };
}
